import { useEffect, useMemo, useState } from 'react';
import { useApi } from '@backstage/core-plugin-api';
import type { CatalogApi } from '@backstage/catalog-client';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import {
  Content,
  Header,
  Link,
  Page,
  Progress,
  ResponseErrorPanel,
  Table,
  TableColumn,
} from '@backstage/core-components';
import { Entity } from '@backstage/catalog-model';
import {
  Box,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Tooltip,
  Typography,
  makeStyles,
} from '@material-ui/core';
import {
  EksUpgradeReport,
  EksUpgradeReportDialog,
} from './EksUpgradeReportDialog';

const GDP_ACCOUNT_ID = '676020893150';
const EKS_FLEET_NAMESPACE = 'eks-platform';

const TEAM_TO_DIVISION: Record<string, string> = {
  sbm: 'Betting',
  sbmi: 'Betting',
  tradingtools: 'Betting',
  'risk-management': 'Betting',
  sma: 'Betting',
  betting: 'Betting',
  'sports-data': 'Data & AI',
  'sports-media': 'Engage and Advertising',
  geniuslive: 'Engage and Advertising',
  devsecops: 'InfraPlatform',
};

interface EksClusterRow {
  catalogName: string;
  namespace: string;
  clusterName: string;
  accountAlias: string;
  division: string;
  environment: string;
  region: string;
  version: string;
  readiness: string;
  upgradeReport?: EksUpgradeReport;
}

const EKS_CLUSTER_PAGE_SIZE = 500;

async function loadFleetEksClusters(
  catalogApi: CatalogApi,
): Promise<Entity[]> {
  const items: Entity[] = [];
  let cursor: string | undefined;

  for (;;) {
    const page = cursor
      ? await catalogApi.queryEntities({ cursor, limit: EKS_CLUSTER_PAGE_SIZE })
      : await catalogApi.queryEntities({
          filter: { kind: 'Resource', 'spec.type': 'eks-cluster' },
          limit: EKS_CLUSTER_PAGE_SIZE,
        });

    items.push(...page.items);
    cursor = page.pageInfo.nextCursor;
    if (!cursor) {
      break;
    }
  }

  return items.filter(isFleetEksCluster);
}

function accountAlias(entity: Entity): string {
  return (
    label(entity, 'aws/account-alias') ||
    label(entity, 'eks-upgrade-planner/account-alias') ||
    label(entity, 'aws/account-id')
  );
}

/** EKS cluster name only (account context comes from the account alias column). */
function clusterDisplayName(entity: Entity): string {
  return (
    label(entity, 'aws/cluster-name') ||
    entity.metadata.title ||
    entity.metadata.name
  );
}

const label = (entity: Entity, key: string) =>
  String(entity.metadata.labels?.[key] ?? '').trim();

function upgradeReadiness(entity: Entity): string {
  const fromLabel = label(entity, 'eks-upgrade/readiness');
  if (fromLabel) {
    return fromLabel;
  }

  const report = (
    entity.spec as { upgradeReport?: { readiness?: string } } | undefined
  )?.upgradeReport;

  return String(report?.readiness ?? '').trim();
}

function getUpgradeReport(entity: Entity): EksUpgradeReport | undefined {
  return (entity.spec as { upgradeReport?: EksUpgradeReport } | undefined)
    ?.upgradeReport;
}

function isFleetEksCluster(entity: Entity): boolean {
  return (entity.metadata.namespace ?? 'default') === EKS_FLEET_NAMESPACE;
}

function resolveDivision(accountId: string, team: string): string {
  if (accountId === GDP_ACCOUNT_ID) {
    return 'Genius Deployment Platform';
  }

  const normalizedTeam = team.toLowerCase();
  return TEAM_TO_DIVISION[normalizedTeam] ?? team;
}

function toRow(entity: Entity): EksClusterRow {
  const namespace = entity.metadata.namespace ?? 'eks-platform';
  const lifecycle = String(
    (entity.spec as { lifecycle?: string } | undefined)?.lifecycle ?? '',
  ).trim();

  const accountId = label(entity, 'aws/account-id');
  const team = label(entity, 'eks/team');

  return {
    catalogName: entity.metadata.name,
    namespace,
    clusterName: clusterDisplayName(entity),
    accountAlias: accountAlias(entity),
    division: resolveDivision(accountId, team),
    environment: label(entity, 'eks/env') || lifecycle,
    region: label(entity, 'aws/region'),
    version: label(entity, 'eks/kubernetes-version'),
    readiness: upgradeReadiness(entity),
    upgradeReport: getUpgradeReport(entity),
  };
}

const useStyles = makeStyles(theme => ({
  filterBar: {
    alignItems: 'center',
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  select: {
    minWidth: 180,
  },
  readinessUpToDate: {
    backgroundColor: '#c8e6c9',
    color: '#1b5e20',
    fontSize: '0.75rem',
    fontWeight: 500,
  },
  readinessAmber: {
    backgroundColor: '#ffca28',
    color: '#5d4037',
    fontSize: '0.75rem',
    fontWeight: 600,
  },
  readinessBlocked: {
    backgroundColor: '#ffcdd2',
    color: '#b71c1c',
    fontSize: '0.75rem',
    fontWeight: 500,
  },
  readinessUnknown: {
    backgroundColor: theme.palette.grey[300],
    color: theme.palette.text.primary,
    fontSize: '0.75rem',
    fontWeight: 500,
  },
  clusterCell: {
    maxWidth: 180,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  nowrapCell: {
    whiteSpace: 'nowrap',
  },
  clickableChip: {
    cursor: 'pointer',
  },
}));

function ReadinessChip({
  readiness,
  classes,
  onClick,
}: {
  readiness: string;
  classes: ReturnType<typeof useStyles>;
  onClick?: () => void;
}) {
  const normalized = readiness.toLowerCase();
  let className = classes.readinessUnknown;

  if (normalized === 'up-to-date') {
    className = classes.readinessUpToDate;
  } else if (
    normalized === 'nodes-ready' ||
    normalized === 'control-plane-ready'
  ) {
    className = classes.readinessAmber;
  } else if (normalized === 'blocked') {
    className = classes.readinessBlocked;
  }

  return (
    <Chip
      size="small"
      label={readiness || 'unknown'}
      className={`${className}${onClick ? ` ${classes.clickableChip}` : ''}`}
      onClick={onClick}
      clickable={Boolean(onClick)}
    />
  );
}

export const EksClustersPage = () => {
  const classes = useStyles();
  const catalogApi = useApi(catalogApiRef);
  const [rows, setRows] = useState<EksClusterRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error>();
  const [selectedDivision, setSelectedDivision] = useState('');
  const [selectedEnvironment, setSelectedEnvironment] = useState('');
  const [selectedReadiness, setSelectedReadiness] = useState('');
  const [selectedCluster, setSelectedCluster] = useState<EksClusterRow | null>(
    null,
  );

  useEffect(() => {
    let active = true;

    (async () => {
      try {
        setLoading(true);
        const items = await loadFleetEksClusters(catalogApi);

        if (active) {
          setRows(items.map(toRow));
        }
      } catch (e: unknown) {
        if (active) {
          setError(e instanceof Error ? e : new Error(String(e)));
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    })();

    return () => {
      active = false;
    };
  }, [catalogApi]);

  const divisionOptions = useMemo(
    () =>
      [...new Set(rows.map(row => row.division).filter(Boolean))].sort(),
    [rows],
  );
  const environmentOptions = useMemo(
    () =>
      [...new Set(rows.map(row => row.environment).filter(Boolean))].sort(),
    [rows],
  );
  const readinessOptions = useMemo(
    () => [...new Set(rows.map(row => row.readiness).filter(Boolean))].sort(),
    [rows],
  );

  const filtered = useMemo(() => {
    let data = rows;
    if (selectedDivision) {
      data = data.filter(row => row.division === selectedDivision);
    }
    if (selectedEnvironment) {
      data = data.filter(row => row.environment === selectedEnvironment);
    }
    if (selectedReadiness) {
      data = data.filter(row => row.readiness === selectedReadiness);
    }
    return data;
  }, [rows, selectedDivision, selectedEnvironment, selectedReadiness]);

  const columns: TableColumn<EksClusterRow>[] = useMemo(
    () => [
      {
        title: 'Account',
        field: 'accountAlias',
        width: '18%',
        cellStyle: { whiteSpace: 'nowrap' },
      },
      {
        title: 'Cluster',
        field: 'clusterName',
        width: '14%',
        cellStyle: { maxWidth: 180 },
        render: (row: EksClusterRow) => (
          <Tooltip title={`${row.clusterName} (${row.catalogName})`}>
            <Box className={classes.clusterCell}>
              <Link
                to={`/catalog/${encodeURIComponent(row.namespace)}/resource/${encodeURIComponent(row.catalogName)}`}
              >
                {row.clusterName}
              </Link>
            </Box>
          </Tooltip>
        ),
      },
      {
        title: 'Division',
        field: 'division',
        width: '16%',
        cellStyle: { whiteSpace: 'nowrap' },
        render: (row: EksClusterRow) => (
          <Typography variant="body2" className={classes.nowrapCell}>
            {row.division}
          </Typography>
        ),
      },
      {
        title: 'Environment',
        field: 'environment',
        width: '8%',
        cellStyle: { whiteSpace: 'nowrap' },
      },
      {
        title: 'Region',
        field: 'region',
        width: '10%',
        cellStyle: { whiteSpace: 'nowrap' },
      },
      {
        title: 'Version',
        field: 'version',
        defaultSort: 'desc',
        width: '7%',
        cellStyle: { whiteSpace: 'nowrap' },
      },
      {
        title: 'Upgrade readiness',
        field: 'readiness',
        render: (row: EksClusterRow) => (
          <ReadinessChip
            readiness={row.readiness}
            classes={classes}
            onClick={() => setSelectedCluster(row)}
          />
        ),
        width: '14%',
      },
    ],
    [classes],
  );

  if (loading) {
    return (
      <Page themeId="tool">
        <Header title="EKS Clusters" />
        <Content>
          <Progress />
        </Content>
      </Page>
    );
  }

  if (error) {
    return (
      <Page themeId="tool">
        <Header title="EKS Clusters" />
        <Content>
          <ResponseErrorPanel error={error} />
        </Content>
      </Page>
    );
  }

  return (
    <Page themeId="tool">
      <Header
        title="EKS Clusters"
        subtitle="Fleet overview from catalog resources (spec.type: eks-cluster)"
      />
      <Content>
        <Box className={classes.filterBar}>
          <FormControl variant="outlined" size="small" className={classes.select}>
            <InputLabel id="eks-division-filter">Division</InputLabel>
            <Select
              labelId="eks-division-filter"
              value={selectedDivision}
              onChange={event =>
                setSelectedDivision(String(event.target.value))
              }
              label="Division"
            >
              <MenuItem value="">All divisions</MenuItem>
              {divisionOptions.map(division => (
                <MenuItem key={division} value={division}>
                  {division}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl variant="outlined" size="small" className={classes.select}>
            <InputLabel id="eks-env-filter">Environment</InputLabel>
            <Select
              labelId="eks-env-filter"
              value={selectedEnvironment}
              onChange={event =>
                setSelectedEnvironment(String(event.target.value))
              }
              label="Environment"
            >
              <MenuItem value="">All environments</MenuItem>
              {environmentOptions.map(environment => (
                <MenuItem key={environment} value={environment}>
                  {environment}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl variant="outlined" size="small" className={classes.select}>
            <InputLabel id="eks-readiness-filter">Upgrade readiness</InputLabel>
            <Select
              labelId="eks-readiness-filter"
              value={selectedReadiness}
              onChange={event =>
                setSelectedReadiness(String(event.target.value))
              }
              label="Upgrade readiness"
            >
              <MenuItem value="">All readiness states</MenuItem>
              {readinessOptions.map(readiness => (
                <MenuItem key={readiness} value={readiness}>
                  {readiness}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        <Table<EksClusterRow>
          title={`EKS Clusters (${filtered.length})`}
          options={{
            paging: true,
            pageSize: 25,
            search: true,
            padding: 'dense',
            thirdSortClick: false,
          }}
          columns={columns}
          data={filtered}
        />

        <EksUpgradeReportDialog
          open={Boolean(selectedCluster)}
          clusterName={selectedCluster?.clusterName ?? ''}
          report={selectedCluster?.upgradeReport}
          onClose={() => setSelectedCluster(null)}
        />
      </Content>
    </Page>
  );
};
